import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  const { t, language } = useLanguage();
  const de = language === 'de';

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <Button asChild variant="ghost" className="mb-8 text-muted-foreground hover:text-foreground" data-testid="back-to-home">
          <Link to="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('auth.backToHome')}
          </Link>
        </Button>

        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-12">
          {de ? 'Datenschutzerklärung' : 'Privacy Policy'}
        </h1>

        <div className="space-y-10 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '1. Verantwortliche Stelle' : '1. Data Controller'}
            </h2>
            <p>
              Endrich Media Agency<br />
              Kemptpark 28<br />
              8310 Kemptthal, {de ? 'Schweiz' : 'Switzerland'}<br />
              <span className="text-muted-foreground">
                {de
                  ? '(Betreiberin der Marke Schoolpix und der Website schoolpix.media)'
                  : '(Operator of the brand Schoolpix and the website schoolpix.media)'}
              </span>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '2. Allgemeine Grundsätze' : '2. General Principles'}
            </h2>
            <p>
              {de
                ? 'Die Endrich Media Agency nimmt den Schutz Ihrer personenbezogenen Daten ernst. Sämtliche Datenverarbeitungen auf dieser Website erfolgen im Einklang mit dem Schweizer Bundesgesetz über den Datenschutz (DSG) und, soweit anwendbar, der EU-Datenschutz-Grundverordnung (DSGVO).'
                : 'Endrich Media Agency takes the protection of your personal data seriously. All data processing on this website is carried out in accordance with the Swiss Federal Act on Data Protection (FADP) and, where applicable, the EU General Data Protection Regulation (GDPR).'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '3. Kontakt per E-Mail' : '3. Contact via Email'}
            </h2>
            <p className="mb-3">
              {de
                ? 'Wenn Sie uns per E-Mail kontaktieren, werden die von Ihnen übermittelten Daten (z. B. Name, E-Mail-Adresse, Nachrichteninhalt) gespeichert und ausschliesslich zur Beantwortung Ihrer Anfrage verwendet. Ohne Ihre Einwilligung geben wir diese Daten nicht an Dritte weiter.'
                : 'When you contact us by email, the data you provide (e.g. name, email address, message content) will be stored and used solely to respond to your enquiry. We do not share this data with third parties without your consent.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {de
                ? 'Rechtsgrundlage: berechtigtes Interesse an der Beantwortung von Anfragen (Art. 6 Abs. 1 lit. f DSGVO / Art. 31 Abs. 1 DSG).'
                : 'Legal basis: legitimate interest in responding to enquiries (Art. 6(1)(f) GDPR / Art. 31(1) FADP).'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '4. Onlineshop und Buchungen' : '4. Online Shop and Bookings'}
            </h2>
            <p className="mb-3">
              {de
                ? 'Bei der Nutzung unseres Onlineshops oder unserer Buchungsdienste erheben wir die zur Vertragserfüllung notwendigen Daten, darunter Name, Adresse, E-Mail-Adresse und Zahlungsinformationen. Diese Daten werden ausschliesslich zur Bearbeitung der Bestellung oder Buchung sowie zur Rechnungsstellung verwendet.'
                : 'When using our online shop or booking services, we collect the data necessary for contract fulfilment, including name, address, email address, and payment information. This data is used exclusively to process the order or booking and for invoicing.'}
            </p>
            <p className="mb-3">
              {de
                ? 'Sofern Drittanbieter für Zahlungen eingesetzt werden (z. B. Stripe, PayPal oder ähnliche), gelten zusätzlich deren Datenschutzrichtlinien. Wir teilen nur die Daten, die für die Zahlungsabwicklung zwingend erforderlich sind.'
                : 'Where third-party payment providers (e.g. Stripe, PayPal, or similar) are used, their privacy policies apply additionally. We only share data that is strictly necessary for payment processing.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {de
                ? 'Rechtsgrundlage: Vertragserfüllung (Art. 6 Abs. 1 lit. b DSGVO / Art. 31 Abs. 2 lit. a DSG). Die gesetzliche Aufbewahrungspflicht für Buchungsdaten beträgt in der Schweiz in der Regel 10 Jahre.'
                : 'Legal basis: contract performance (Art. 6(1)(b) GDPR / Art. 31(2)(a) FADP). Statutory retention obligations for booking data are generally 10 years under Swiss law.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '5. Cookies und Google Analytics' : '5. Cookies and Google Analytics'}
            </h2>
            <p className="mb-3">
              {de
                ? 'Diese Website verwendet Cookies — kleine Textdateien, die von Ihrem Browser auf Ihrem Gerät gespeichert werden. Technisch notwendige Cookies werden ohne Einwilligung gesetzt. Für analytische Cookies holen wir vorab Ihre Zustimmung ein.'
                : 'This website uses cookies — small text files stored on your device by your browser. Technically necessary cookies are set without consent. For analytical cookies, we obtain your prior consent.'}
            </p>
            <p className="mb-3">
              {de
                ? 'Wir verwenden Google Analytics (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland) zur Analyse des Nutzerverhaltens auf schoolpix.media. Dabei werden anonymisierte IP-Adressen, Seitenaufrufe, Verweildauer und Geräteinformationen erfasst. Diese Daten können an Google-Server, einschliesslich in den USA, übertragen werden.'
                : 'We use Google Analytics (Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland) to analyse user behaviour on schoolpix.media. Data collected includes anonymised IP addresses, page views, time on site, and device information. This data may be transferred to Google servers, including in the United States.'}
            </p>
            <p className="mb-3">
              {de
                ? 'Google Analytics wird nur mit Ihrer ausdrücklichen Einwilligung aktiviert. Die IP-Anonymisierung ist aktiviert. Sie können Ihre Einwilligung jederzeit über die Cookie-Einstellungen auf dieser Website widerrufen oder das Browser-Add-on zur Deaktivierung von Google Analytics installieren.'
                : 'Google Analytics is only activated with your explicit consent. IP anonymisation is enabled. You may withdraw your consent at any time via the cookie settings on this website, or by installing the Google Analytics opt-out browser add-on.'}
            </p>
            <p className="text-sm text-muted-foreground">
              {de ? 'Weitere Informationen: ' : 'Further information: '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                policies.google.com/privacy
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '6. Weitergabe an Dritte' : '6. Disclosure to Third Parties'}
            </h2>
            <p>
              {de
                ? 'Ihre personenbezogenen Daten werden nur an Dritte weitergegeben, wenn dies zur Vertragserfüllung notwendig ist, Sie Ihre Einwilligung erteilt haben oder eine gesetzliche Verpflichtung besteht. Wir verkaufen Ihre Daten nicht.'
                : 'Your personal data will only be disclosed to third parties where this is necessary for contract fulfilment, where you have given your consent, or where there is a legal obligation to do so. We do not sell your data.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '7. Datensicherheit' : '7. Data Security'}
            </h2>
            <p>
              {de
                ? 'Wir setzen geeignete technische und organisatorische Sicherheitsmassnahmen ein, um Ihre Daten vor unbefugtem Zugriff, Verlust oder Missbrauch zu schützen. Über diese Website übermittelte Daten werden mittels HTTPS verschlüsselt.'
                : 'We implement appropriate technical and organisational security measures to protect your data against unauthorised access, loss, or misuse. Data transmitted via this website is encrypted using HTTPS.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '8. Ihre Rechte' : '8. Your Rights'}
            </h2>
            <p className="mb-3">{de ? 'Sie haben das Recht auf:' : 'You have the right to:'}</p>
            <ul className="list-none space-y-2 pl-4">
              <li className="flex gap-2"><span className="text-primary">—</span> {de ? 'Auskunft über die bei uns gespeicherten personenbezogenen Daten' : 'Access the personal data we hold about you'}</li>
              <li className="flex gap-2"><span className="text-primary">—</span> {de ? 'Berichtigung unrichtiger Daten' : 'Rectification of inaccurate data'}</li>
              <li className="flex gap-2"><span className="text-primary">—</span> {de ? 'Löschung Ihrer Daten, vorbehaltlich gesetzlicher Aufbewahrungspflichten' : 'Erasure of your data, subject to any statutory retention obligations'}</li>
              <li className="flex gap-2"><span className="text-primary">—</span> {de ? 'Widerruf einer erteilten Einwilligung mit Wirkung für die Zukunft' : 'Withdrawal of any consent given, with effect for the future'}</li>
              <li className="flex gap-2"><span className="text-primary">—</span> {de ? 'Einreichung einer Beschwerde beim Eidgenössischen Datenschutz- und Öffentlichkeitsbeauftragten (EDÖB)' : 'Lodge a complaint with the Swiss Federal Data Protection and Information Commissioner (FDPIC)'}</li>
            </ul>
            <p className="mt-3">
              {de
                ? 'Zur Ausübung Ihrer Rechte kontaktieren Sie bitte die Endrich Media Agency per E-Mail.'
                : 'To exercise your rights, please contact Endrich Media Agency by email.'}
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-3 uppercase tracking-wider text-primary">
              {de ? '9. Änderungen dieser Datenschutzerklärung' : '9. Changes to This Policy'}
            </h2>
            <p>
              {de
                ? 'Wir behalten uns das Recht vor, diese Datenschutzerklärung jederzeit zu aktualisieren, um Änderungen der rechtlichen Anforderungen oder neuer Website-Funktionen zu berücksichtigen. Es gilt die jeweils auf schoolpix.media veröffentlichte Fassung.'
                : 'We reserve the right to update this privacy policy at any time in order to reflect changes in legal requirements or new website features. The version currently published on schoolpix.media shall apply.'}
            </p>
          </section>

          <div className="pt-6 border-t border-border text-sm text-muted-foreground">
            {de ? 'Stand März 2026' : 'As of March 2026'} · Endrich Media Agency, Kemptthal
          </div>
        </div>
      </div>
    </div>
  );
}
